import { useIntl } from 'react-intl';
import { FC, useState } from 'react';
import { IClinicMember } from '../../types/clinic';
import { useAppSelector } from '../../utils/hooks';
import { useGetClinicQuery } from '../../redux/api/clinic/clinicApi';
import { UserPermissionEnum } from '../../constants/enums';
import InviteUserForm from '../../components/UsersTabForms/InviteUserForm';
import EditUserForm from '../../components/UsersTabForms/EditUserForm';
import ErrorPanel from '../../components/ErrorPanel/ErrorPanel';
import Spinner from '../../components/Shared/Loaders/Spinner';
import styles from './ClinicPage.module.scss';
import cn from 'classnames';

const UsersTabPanel: FC = () => {
  const intl = useIntl();
  const { user } = useAppSelector((state) => state.user);
  const { data: clinic, isLoading, error, isFetching } = useGetClinicQuery();

  const userPermission = user.sourceUserGroup.userPermission;

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<IClinicMember | undefined>({
    id: '',
    profession: '',
    firstName: '',
    lastName: '',
    email: '',
    permission: '',
  });

  const openInviteModalHandler = () => setIsInviteModalOpen(true);
  const closeInviteModalHandler = () => setIsInviteModalOpen(false);

  const openEditModalHandler = (user: IClinicMember) => {
    setEditUser(user);
    setIsEditModalOpen(true);
  };
  const closeEditModalHandler = () => {
    setEditUser(undefined);
    setIsEditModalOpen(false);
  };

  if (error && 'status' in error)
    return (
      <section className={styles.usersSection}>
        <ErrorPanel error={error} />{' '}
      </section>
    );

  return (
    <>
      <section className={styles.usersSection}>
        {isLoading || isFetching ? (
          <Spinner purple />
        ) : (
          <>
            {userPermission === UserPermissionEnum.ADMIN && (
              <div className={styles.filterPanel}>
                <button
                  className={cn(styles.btn, styles.inviteBtn)}
                  onClick={openInviteModalHandler}
                >
                  <div className={styles.plusIcon} />
                  {intl.formatMessage({
                    id: 'users.inviteUser',
                    defaultMessage: 'Invite user',
                  })}
                </button>
              </div>
            )}
            <div className={styles.tableContainer}>
              <table>
                <thead>
                  <tr>
                    <th align="left">
                      {intl.formatMessage({ id: 'general.name', defaultMessage: 'Name' })}
                    </th>
                    <th align="left">
                      {intl.formatMessage({
                        id: 'general.profession',
                        defaultMessage: 'Profession',
                      })}
                    </th>
                    <th align="left">
                      {intl.formatMessage({
                        id: 'general.email',
                        defaultMessage: 'Email',
                      })}
                    </th>
                    <th align="left">
                      {intl.formatMessage({
                        id: 'general.userPermission',
                        defaultMessage: 'User permission',
                      })}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clinic?.members.map((u, i) => (
                    <tr key={i}>
                      <td>{u.id ? `${u.firstName + ' ' + u.lastName}` : ''}</td>
                      <td>{u.id ? u.profession : ''}</td>
                      <td>{u.email}</td>
                      <td>
                        {u.id
                          ? u.permission
                          : intl.formatMessage({
                              id: 'users.pendingInvite',
                              defaultMessage: 'Pending invite',
                            })}
                      </td>
                      <td>
                        {user.id !== u.id && userPermission === UserPermissionEnum.ADMIN ? (
                          <div
                            className={styles.settingsBtn}
                            onClick={() => openEditModalHandler(u)}
                          >
                            <div className={styles.dot} />
                            <div className={styles.dot} />
                            <div className={styles.dot} />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <InviteUserForm
              isOpen={isInviteModalOpen}
              userGroupId={clinic?.userGroupId || ''}
              onDialogCloseClick={closeInviteModalHandler}
            />
            <EditUserForm
              user={editUser}
              isOpen={isEditModalOpen}
              userGroupId={clinic?.userGroupId || ''}
              onDialogCloseClick={closeEditModalHandler}
            />
          </>
        )}
      </section>
    </>
  );
};

export default UsersTabPanel;
